const hasRelationship = require("./hasRelationship");
const mockListData = require("./mockListData");
const getNavItem = require("./getNavItem");
const transformBannerData = require("./transformBannerData");
const extractAnchorsData = require("./extractAnchorsData");
const findEntityDates = require("./findEntityDates");
const findFeaturedImage = require("./findFeaturedImage");
const findImage = require("./findImage");
const getCompoundEntityTitle = require("./getCompoundEntityTitle");
const transformVideoData = require("./transformVideoData");
const transformFieldsDisplayData = require("./transformFieldsDisplayData");

/**
 * Inject node data into relationships data
 *
 * @param entity: an drupal entity object
 * @returns
 */
module.exports = function transformSingleEntity({
	entity,
	bundles,
	typeDefinitions,
	themeConfig,
	configs,
	sitemap,
}) {
	let full = { ...entity };

	full.bundle = entity.type.includes("--") ? entity.type.split("--")[1] : null;

	/**
	 * includes: a plain object like {uuid: entity, ...} with all entities that appear inside the main entity
	 * relationships: main entity's relationships object
	 * attributes: main entity's attributes object
	 */
	const { includes, relationships, attributes, config } = entity;

	full.sidebarStyle = themeConfig?.customOptions?.sidebarStyle || "default";
	full.isReversed = themeConfig?.customOptions?.reverseDate || false;
	full.logoPrint = themeConfig?.customOptions?.logoPrint || "/logo.png";
	full.isSideShare = themeConfig?.customOptions?.shareLocation === "side";

	/**
	 * build complex entity titles if needed
	 */

	if (config && config.titleSchema) {
		full.title = getCompoundEntityTitle(config.titleSchema, attributes);
	}

	full.vCard = config && config.options && config.options.vCard ? true : false;

	full.hasFixedFeaturedImage =
		config && config.options && config.options.fixedFeaturedImage ? true : false;

	if (includes && relationships && Object.keys(includes).length > 0) {
		full.relationships = fill(relationships, entity.id, false, "main");
	}

	function fill(rels, main_id, restrict, for_key = null) {
		// console.log("fill: ", for_key, "restrict: ", restrict);
		const restrict_rels = [
			"field_entities_list_by_content",
			"field_entities_list_by_specific",
		];
		let full_rels = {};
		for (let [rel_field_name, rel_info] of Object.entries(rels)) {
			if (rel_info.data) {
				if (Array.isArray(rel_info.data)) {
					full_rels[rel_field_name] = { data: [...rel_info.data] };
					for (const i in rel_info.data) {
						const a_rel_entity = rel_info.data[i];
						if (
							a_rel_entity.type &&
							a_rel_entity.id &&
							typeof includes[a_rel_entity.id] !== "undefined"
						) {
							full_rels[rel_field_name]["data"][i] = {
								...a_rel_entity,
								...includes[a_rel_entity.id],
							};
							if (
								a_rel_entity.id !== main_id &&
								full_rels[rel_field_name]["data"][i].relationships &&
								!restrict
							) {
								full_rels[rel_field_name]["data"][i].relationships = fill(
									full_rels[rel_field_name]["data"][i].relationships,
									main_id,
									restrict_rels.includes(rel_field_name),
									rel_field_name
								);
							}
						}
					}
				} else if (
					rel_info.data.type &&
					rel_info.data.id &&
					typeof includes[rel_info.data.id] !== "undefined"
				) {
					full_rels[rel_field_name] = {};
					full_rels[rel_field_name]["data"] = {
						...rel_info.data,
						...includes[rel_info.data.id],
					};
					if (
						rel_info.data.id !== main_id &&
						full_rels[rel_field_name]["data"].relationships &&
						!restrict
					) {
						full_rels[rel_field_name]["data"].relationships = fill(
							full_rels[rel_field_name]["data"].relationships,
							main_id,
							restrict_rels.includes(rel_field_name),
							rel_field_name
						);
					}
				}
			}
		}
		return full_rels;
	}

	if (attributes) {
		full.hasBody = attributes.body !== null;
		full.isHome = attributes.path && attributes.path.alias === "/home";
		full.dates = findEntityDates(attributes, config);
		full.hasDates = !!full.dates && !config.options?.hideDate;
		full.dateFormat = config && config.dateFormat ? config.dateFormat : "D";
		full.changed = attributes.changed;
		full.newBadge = attributes.field_product_new_badge;

		full.summary = attributes.field_summary ? attributes.field_summary : null;

		if (config && config.titleAppend && attributes[config.titleAppend]) {
			full.title_append = attributes[config.titleAppend];
		}

		if (config && config.shareButtons && !attributes.field_disable_share) {
			full.shareButtons = config.shareButtons;
		}

		full.sidebarSize =
			config && config.options && config.options.sidebarSize
				? config.options.sidebarSize
				: attributes.field_section_sidebar_size
				? attributes.field_section_sidebar_size
				: false;
		full.gutterSize = attributes.field_gutter_size ? attributes.field_gutter_size : 4;
		full.hasExternalLink = attributes.field_to ? true : false;
		if (attributes.field_to) {
			full.externalLink = {
				to: attributes.field_to.uri,
				toLabel: attributes.field_to.title,
				fullWidth: true,
				variant: "contained",
				size: "large",
			};
		}
		full.hasIntro = attributes.field_intro ? true : false;
		if (full.hasIntro) {
			let introLabel = themeConfig?.customOptions?.introLabel || "";
			let cleanIntro = attributes.field_intro.value.replace(/(<p[^>]+?>|<p>)/gim, "");
			cleanIntro = cleanIntro.replace(/(<\/p>)/gim, "<br />");
			full.intro = `<p><strong>${introLabel}</strong> ${cleanIntro}</p>`;
		}
		full.addToCart = attributes.field_cart_item ? true : false;
	}

	if (relationships) {
		/**
		 * Find cover image
		 */
		let _image_rel = findFeaturedImage(full.relationships);
		if (_image_rel) {
			let image = findImage(_image_rel);
			if (image) {
				full.image = image;
			}
		}

		/**
		 * Find main video
		 */
		full.hasVideo =
			relationships.field_media_video && relationships.field_media_video.data !== null
				? true
				: false;

		if (full.hasVideo) {
			full.video = transformVideoData(full);
		}

		/**
		 * Define general properties for single body layout renderization
		 */
		full.hasMainSection = hasRelationship(relationships.field_section_main);
		full.hasClosingSection = hasRelationship(relationships.field_section_closing);
		full.hasMedia = hasRelationship(relationships.field_media);
		full.hasBodyMedia = full.hasMedia && !config.options?.mediaOnSidebar;
		full.hasSidebarMedia = full.hasMedia && config.options?.mediaOnSidebar;
		full.hasSidebar = hasRelationship(relationships.field_section_sidebar);
		full.hasBanner = hasRelationship(relationships.field_heading_banner);

		if (
			config &&
			config.options &&
			(config.options.singleLayout || config.options.bannerLayout)
		) {
			full.bannerLayout = config.options.singleLayout
				? config.options.singleLayout
				: config.options.bannerLayout;
		} else {
			full.bannerLayout =
				attributes && attributes.field_heading_banner_layout
					? attributes.field_heading_banner_layout
					: null;
		}

		if (full.hasBanner && full.bannerLayout && full.bannerLayout !== "blog") {
			full.hasBanner = false;
		}

		if (hasRelationship(relationships.field_heading_banner)) {
			full.banner = transformBannerData(full.relationships.field_heading_banner.data);
		}

		full.disableTitleBar =
			(attributes && attributes.field_disable_title_bar) || full.bannerLayout === "blog"
				? true
				: false;

		/**
		 * Define bundle relationships to be shown as tags
		 */
		if (bundles) {
			full.tags = bundles
				.filter(
					(b) =>
						typeof full.relationships[`field_${b}`] !== "undefined" &&
						!!full.relationships[`field_${b}`].data &&
						((config.options?.tagsHide && !config.options.tagsHide.includes(b)) ||
							!config.options?.tagsHide)
				)
				.map((b) => {
					let def = typeDefinitions.find((t) => t.bundle === b);
					if (!def) return null;
					let _items = Array.isArray(full.relationships[`field_${b}`].data)
						? full.relationships[`field_${b}`].data
						: [full.relationships[`field_${b}`].data];
					let items = _items
						.filter(
							(itm) =>
								itm.id != undefined && itm.id != "missing" && itm.attributes != undefined
						)
						.map((itm) => {
							let title = itm.attributes.name
								? itm.attributes.name
								: itm.attributes.title;
							let nav_item = getNavItem(itm, sitemap);
							if (!nav_item) {
								nav_item = {
									slug: `${
										full.private ? process.env.CLF_PRIVATE_ROUTE : ""
									}/search?filter=field_${def.bundle}&field_${
										def.bundle
									}=${encodeURIComponent(title)}`,
								};
							}
							if (nav_item) {
								return {
									id: itm.id,
									title,
									slug: nav_item.slug,
								};
							}
						})
						.filter((itm) => itm !== null);
					if (items.length === 0) return null;

					return {
						bundle: b,
						label: def.name,
						items,
					};
				})
				.filter((g) => g !== null);

			if (full.tags.length > 0) {
				full.hasTags = true;
				full.tagsPosition =
					themeConfig &&
					themeConfig.customOptions &&
					themeConfig.customOptions.tagsPosition
						? themeConfig.customOptions.tagsPosition
						: "side";
			}
		}

		/**
		 * Extract fields display data to be prepended/appended to the body
		 */

		if (config && config.fieldsDisplay) {
			["prepend", "append"].forEach((position) => {
				if (config.fieldsDisplay[position]) {
					let fieldsDisplayData = transformFieldsDisplayData({
						entity: {
							id: `list-position-${position}`,
							type: "node--fields_display",
							attributes: {
								title: config.fieldsDisplay[position].title,
								field_card_style: config.fieldsDisplay[position].style,
								field_display_title: config.fieldsDisplay[position].display_title,
							},
						},
						singleAttributes: full.attributes,
						singleRelationships: full.relationships,
						fieldList: config.fieldsDisplay[position].fields_list,
						bundles,
						configs,
					});
					if (fieldsDisplayData.fields.length > 0) {
						full.hasFieldsDisplay = true;
						if (!full.fieldsDisplay) {
							full.fieldsDisplay = {};
						}
						full.fieldsDisplay[position] = {
							...config.fieldsDisplay[position],
							...fieldsDisplayData,
						};
					}
				}
			});
		}

		/**
		 * Extract anchors data from for AnchorNav links
		 */

		let anchors = [];
		if (full.hasMainSection) {
			anchors = extractAnchorsData(full.relationships.field_section_main.data);
		}
		if (full.hasClosingSection) {
			anchors = anchors.concat(
				extractAnchorsData(full.relationships.field_section_closing.data)
			);
		}
		full.anchors = anchors;
	}

	/**
	 * If hasMedia, format data to mimic an Entities List
	 */
	if (full.hasMedia) {
		full.media = mockListData(full);
	}

	return full;
};
